import React, { forwardRef, useEffect, useRef } from "react";

export interface MediaMtxWebRtcVideoProps extends Omit<React.VideoHTMLAttributes<HTMLVideoElement>, "src"> {
    mediaMtxWebRtcUrl: string
}

const MediaMtxWebRtcVideoInner = ({ mediaMtxWebRtcUrl, ...props }: MediaMtxWebRtcVideoProps, ref: React.ForwardedRef<HTMLVideoElement>) => {
    const url = new URL(mediaMtxWebRtcUrl)

    const retryPause = 2000;

    const video = useRef<HTMLVideoElement | null>(null)
    // const message = document.getElementById('message');

    let pc: any = null;
    let restartTimeout: any = null;
    let sessionUrl: any = null;
    let offerData: any = null;
    let queuedCandidates: any = [];
    let defaultControls = false;

    const setMessage = (str: any) => {
        // if (str !== '') {
        //     video.current!.controls = false;
        // } else {
        //     video.current!.controls = defaultControls;
        // }
        // message.innerText = str;
    };

    const unquoteCredential = (v: any) => (
        JSON.parse(`"${v}"`)
    );

    const linkToIceServers = (links: any) => (
        (links !== null) ? links.split(', ').map((link: any) => {
            const m = link.match(/^<(.+?)>; rel="ice-server"(; username="(.*?)"; credential="(.*?)"; credential-type="password")?/i);
            const ret: any = {
                urls: [m[1]],
            };

            if (m[3] !== undefined) {
                ret.username = unquoteCredential(m[3]);
                ret.credential = unquoteCredential(m[4]);
                ret.credentialType = 'password';
            }

            return ret;
        }) : []
    );

    const parseOffer = (offer: any) => {
        const ret = {
            iceUfrag: '',
            icePwd: '',
            medias: [],
        };

        for (const line of offer.split('\r\n')) {
            if (line.startsWith('m=')) {
                (ret.medias as any).push(line.slice('m='.length));
            } else if (ret.iceUfrag === '' && line.startsWith('a=ice-ufrag:')) {
                ret.iceUfrag = line.slice('a=ice-ufrag:'.length);
            } else if (ret.icePwd === '' && line.startsWith('a=ice-pwd:')) {
                ret.icePwd = line.slice('a=ice-pwd:'.length);
            }
        }

        return ret;
    };

    const enableStereoOpus = (section: any) => {
        let opusPayloadFormat = '';
        let lines = section.split('\r\n');

        for (let i = 0; i < lines.length; i++) {
            if (lines[i].startsWith('a=rtpmap:') && lines[i].toLowerCase().includes('opus/')) {
                opusPayloadFormat = lines[i].slice('a=rtpmap:'.length).split(' ')[0];
                break;
            }
        }

        if (opusPayloadFormat === '') {
            return section;
        }

        for (let i = 0; i < lines.length; i++) {
            if (lines[i].startsWith('a=fmtp:' + opusPayloadFormat + ' ')) {
                if (!lines[i].includes('stereo')) {
                    lines[i] += ';stereo=1';
                }
                if (!lines[i].includes('sprop-stereo')) {
                    lines[i] += ';sprop-stereo=1';
                }
            }
        }

        return lines.join('\r\n');
    };

    const editOffer = (offer: any) => {
        const sections = offer.sdp.split('m=');

        for (let i = 0; i < sections.length; i++) {
            const section = sections[i];
            if (section.startsWith('audio')) {
                sections[i] = enableStereoOpus(section);
            }
        }

        offer.sdp = sections.join('m=');
        offer.sdp = offer.sdp.replace(/a=ice-ufrag:([a-z0-9]*)\r\n/g, "a=ice-ufrag:$1\r\na=ice-options:trickle\r\n")
    };

    const generateSdpFragment = (od: any, candidates: any) => {
        const candidatesByMedia: any = {};
        for (const candidate of candidates) {
            const mid = candidate.sdpMLineIndex;
            if (candidatesByMedia[mid] === undefined) {
                candidatesByMedia[mid] = [];
            }
            candidatesByMedia[mid].push(candidate);
        }

        let frag = 'a=ice-ufrag:' + od.iceUfrag + '\r\n'
            + 'a=ice-pwd:' + od.icePwd + '\r\n'
            + 'a=ice-options:trickle\r\n';

        let mid = 0;

        for (const media of od.medias) {
            if (candidatesByMedia[mid] !== undefined) {
                frag += 'm=' + media + '\r\n'
                    + 'a=mid:' + mid + '\r\n';

                for (const candidate of candidatesByMedia[mid]) {
                    frag += 'a=' + candidate.candidate + '\r\n';
                }
            }
            mid++;
        }

        return frag;
    };

    const loadStream = () => {
        requestICEServers();
    };

    const onError = (err: any) => {
        if (restartTimeout === null) {
            setMessage(err + ', retrying in some seconds');

            if (pc !== null) {
                pc.close();
                pc = null;
            }

            restartTimeout = window.setTimeout(() => {
                restartTimeout = null;
                loadStream();
            }, retryPause);

            if (sessionUrl) {
                fetch(sessionUrl, {
                    method: 'DELETE',
                });
            }
            sessionUrl = '';

            queuedCandidates = [];
        }
    };

    const sendLocalCandidates = (candidates: any) => {
        if (sessionUrl)
            fetch(sessionUrl + url.search, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/trickle-ice-sdpfrag',
                    'If-Match': '*',
                },
                body: generateSdpFragment(offerData, candidates),
            })
                .then((res) => {
                    switch (res.status) {
                        case 204:
                            break;
                        case 404:
                            throw new Error('stream not found');
                        default:
                            throw new Error(`bad status code ${res.status}`);
                    }
                })
                .catch((err) => {
                    onError(err.toString());
                });
    };

    const onLocalCandidate = (evt: any) => {
        if (restartTimeout !== null) {
            return;
        }

        if (evt.candidate !== null) {
            if (sessionUrl === '') {
                queuedCandidates.push(evt.candidate);
            } else {
                sendLocalCandidates([evt.candidate])
            }
        }
    };

    const onRemoteAnswer = (sdp: any) => {
        if (restartTimeout !== null) {
            return;
        }

        pc.setRemoteDescription(new RTCSessionDescription({
            type: 'answer',
            sdp,
        }));

        if (queuedCandidates.length !== 0) {
            sendLocalCandidates(queuedCandidates);
            queuedCandidates = [];
        }
    };

    const sendOffer = (offer: any) => {
        fetch(new URL('whep', url.href) + url.search, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/sdp',
            },
            body: offer.sdp,
        })
            .then((res) => {
                switch (res.status) {
                    case 201:
                        break;
                    case 404:
                        throw new Error('stream not found');
                    default:
                        throw new Error(`bad status code ${res.status}`);
                }
                sessionUrl = new URL("/web-rtc" + res.headers.get('location') as any, url.href).toString();
                return res.text();
            })
            .then((sdp) => onRemoteAnswer(sdp))
            .catch((err) => {
                onError(err.toString());
            });
    };

    const createOffer = () => {
        pc.createOffer()
            .then((offer: any) => {
                editOffer(offer);
                offerData = parseOffer(offer.sdp) as any;
                pc.setLocalDescription(offer);
                sendOffer(offer);
            });
    };

    const onConnectionState = () => {
        if (restartTimeout !== null) {
            return;
        }

        if (pc.iceConnectionState === 'disconnected') {
            onError('peer connection disconnected');
        }
    };

    const onTrack = (evt: any) => {
        // setMessage('');
        video.current!.srcObject = evt.streams[0];
    };

    const requestICEServers = () => {
        fetch(new URL('whep', url.href) + url.search, {
            method: 'OPTIONS',
        })
            .then((res) => {
                pc = new RTCPeerConnection({
                    iceServers: linkToIceServers(res.headers.get('Link')),
                    // https://webrtc.org/getting-started/unified-plan-transition-guide
                    sdpSemantics: 'unified-plan',
                } as any);

                const direction = 'sendrecv';
                pc.addTransceiver('video', { direction });
                pc.addTransceiver('audio', { direction });

                pc.onicecandidate = (evt: any) => onLocalCandidate(evt);
                pc.oniceconnectionstatechange = () => onConnectionState();
                pc.ontrack = (evt: any) => onTrack(evt);

                createOffer();
            })
            .catch((err) => {
                onError(err.toString());
            });
    };

    const parseBoolString = (str: any, defaultVal: any) => {
        str = (str || '');

        if (['1', 'yes', 'true'].includes(str.toLowerCase())) {
            return true;
        }
        if (['0', 'no', 'false'].includes(str.toLowerCase())) {
            return false;
        }
        return defaultVal;
    };

    const loadAttributesFromQuery = () => {
        const params = new URLSearchParams(mediaMtxWebRtcUrl);
        video.current!.controls = parseBoolString(params.get('controls'), true);
        video.current!.muted = parseBoolString(params.get('muted'), true);
        video.current!.autoplay = parseBoolString(params.get('autoplay'), true);
        video.current!.playsInline = parseBoolString(params.get('playsinline'), true);
        defaultControls = video.current!.controls;
    };

    const lock = useRef({ state: false })
    const init = () => {
        if (!lock.current.state) {
            lock.current.state = true
            loadAttributesFromQuery();
            loadStream();
        }
    };

    useEffect(init, [])

    return (
        <video
            ref={
                (node) => {
                    video.current = node;
                    if (typeof ref === 'function') {
                        ref(node);
                    } else if (ref) {
                        ref.current = node;
                    }
                }}
            {...props}>
        </video>
    )
}

const MediaMtxWebRtcVideo = forwardRef(MediaMtxWebRtcVideoInner)

export default MediaMtxWebRtcVideo